import { useState } from 'react';
import {
  useDecideApprovalMutation,
  useGetCurrentUserQuery,
  useGetRunApprovalQuery, useDoubtStatusQuery } from '@/services/api';
import { ErrorState } from './ErrorState';
import { DeliverabilityWarning } from './DeliverabilityWarning';
import { ResolvabilityWarning } from './ResolvabilityWarning';
import { SkeletonGridRow } from './Skeleton';
import { formatDuration, formatTimestamp } from '@/utils/format';
import { parseApiError } from '@/utils/apiError';
import type { ApprovalDecision, ApprovalRequestDto, CurrentUserDto } from '@/types/api';

function decideErrorMessage(err: unknown): string {
  const parsed = parseApiError(err);
  const blob = `${parsed.code ?? ''} ${parsed.field ?? ''} ${parsed.reason ?? ''} ${parsed.message ?? ''}`;
  if (blob.includes('not_assigned_to_stage')) {
    return 'You are not assigned to this stage — only an assigned approver or an admin can decide it.';
  }
  if (blob.includes('not_a_project_member')) {
    return 'You are not a member of this project, so you cannot decide its approvals.';
  }
  return 'The decision could not be recorded — you may not have permission for this stage. Please try again or contact an admin.';
}

export function ApprovalPanel({
  runId,
  refreshIntervalMs = 0
}: {
  runId: string;
  refreshIntervalMs?: number;
}): JSX.Element {
  const resp = useGetRunApprovalQuery(runId, {
    pollingInterval: refreshIntervalMs,
    skipPollingIfUnfocused: true
  });
  const userResp = useGetCurrentUserQuery();

  if (resp.isLoading) {
    return (
      <div data-testid="approval-panel-loading">
        <SkeletonGridRow cols={3} />
      </div>
    );
  }
  if (resp.isError && parseApiError(resp.error).status === 404) {
    return <></>;
  }
  if (resp.isError || !resp.data) {
    return (
      <ErrorState
        title="Couldn't load the approval request"
        body="The approval request couldn't be loaded right now. Please retry."
        onRetry={() => void resp.refetch()}
      />
    );
  }
  return (
    <ApprovalDecisionPanel
      approval={resp.data}
      currentUser={userResp.data ?? null}
    />
  );
}

import { DoubtPanel } from './DoubtPanel';

function ApprovalDecisionPanel({
  approval,
  currentUser
}: {
  approval: ApprovalRequestDto;
  currentUser: CurrentUserDto | null;
}): JSX.Element {
  const [decide, result] = useDecideApprovalMutation();
  const [reason, setReason] = useState('');
  const [submittedDecision, setSubmittedDecision] = useState<ApprovalDecision | null>(null);
  const isStepThrough = approval.gate_mode === 'step_through';
  const hasDecideCapability = currentUser?.capabilities.includes('approval:decide') ?? false;
  const serverAllowsDecide = approval.can_decide !== false;
  const canDecide = hasDecideCapability && serverAllowsDecide;
  const denyReason =
    approval.decide_denied_reason === 'not_assigned_to_stage'
      ? 'You are not assigned to this stage — only an assigned approver or an admin can decide it.'
      : approval.decide_denied_reason === 'not_a_project_member'
        ? 'You are not a member of this project, so you cannot decide its approvals.'
        : approval.decide_denied_reason === 'role_not_permitted' || !hasDecideCapability
          ? 'You do not have permission to decide approvals.'
          : '';
  const reasonFilled = reason.trim().length > 0;
  const { data: doubtState } = useDoubtStatusQuery(approval.approval_id, {
    pollingInterval: 5000,
    refetchOnMountOrArgChange: true
  });
  const doubtAnalysing = doubtState?.doubt?.status === 'analysing';
  const verdictAwaitingDecision = doubtState?.doubt?.status === 'verdict_pending';
  const slaMinutes = approval.sla_minutes;
  const slaBreached = approval.breach_notified === true;

  const submit = (decision: ApprovalDecision): void => {
    if (!canDecide || !reasonFilled) return;
    setSubmittedDecision(decision);
    void decide({ approval_id: approval.approval_id, decision, reason: reason.trim() });
  };

  return (
    <article
      className="bg-panel border border-border rounded-lg overflow-hidden mb-4"
      data-testid="approval-panel"
    >
      <header className="px-4 py-3 border-b border-border flex items-center gap-3">
        <div className="font-semibold text-[14px]">
          {isStepThrough
            ? `Review required · paused after ${approval.paused_at_stage || 'stage'}`
            : `Approval required · ${approval.pipeline_run_id}`}
        </div>
        {slaBreached ? (
          <span className="text-[11px] px-2 py-0.5 rounded-[10px] font-medium bg-err/15 text-err">
            paused · SLA breached
          </span>
        ) : (
          <span className="text-[11px] px-2 py-0.5 rounded-[10px] font-medium bg-warn/15 text-warn">
            paused_for_approval
          </span>
        )}
      </header>

      <DeliverabilityWarning deliverability={approval.deliverability} />
      <ResolvabilityWarning resolvability={approval.resolvability} />
      <div className="p-4">
        <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-panel-2 rounded-md">
          <Field label="paused_at_stage" value={approval.paused_at_stage} mono />
          <Field label="approval_id" value={approval.approval_id} mono />
          <Field label="requested_by" value={`${approval.requested_by_agent_role} agent`} warn />
        </div>
        <div className="grid grid-cols-1 gap-4 mb-4 p-3 bg-panel-2 rounded-md">
          <Field label="reason" value={approval.reason} />
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-panel-2 rounded-md">
          <Field label="paused_at" value={formatTimestamp(approval.paused_at)} />
          <Field
            label="paused_at → waiting"
            value={formatDuration(approval.paused_at, null)}
          />
          <Field
            label="turnaround (TAT)"
            value={formatDuration(approval.paused_at, null)}
            mono
          />
        </div>
        {slaMinutes !== undefined ? (
          <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-panel-2 rounded-md">
            <Field label="SLA target" value={`${slaMinutes} min`} warn={slaBreached} />
            <Field
              label="SLA status"
              value={slaBreached ? 'breached' : 'within target'}
              warn={slaBreached}
            />
          </div>
        ) : null}

        {canDecide && !verdictAwaitingDecision ? (
          <div className="mb-4">
            <label
              htmlFor="approval-reason"
              className="block text-[10.5px] text-text-muted uppercase tracking-wider mb-1"
            >
              reason <span className="text-err">*</span> (required for approve and deny)
            </label>
            <textarea
              id="approval-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Explain your decision — this is recorded in the run Logs and the Audit Log."
              className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-[12.5px] text-text resize-y focus:outline-none focus:border-text"
              data-testid="approval-reason-input"
            />
          </div>
        ) : null}

        <div
          className="flex flex-wrap gap-2.5 items-center pt-3.5 border-t border-border"
          data-testid="approval-action-row"
        >
          {canDecide && verdictAwaitingDecision ? (
            <span className="text-[12px] text-text-muted" data-testid="approval-deferred-to-verdict">
              The investigation has finished. Choose what to do below — that decision also
              approves or stops this run.
            </span>
          ) : canDecide ? (
            <>
              <button
                type="button"
                onClick={() => submit('approved')}
                disabled={result.isLoading || !reasonFilled || doubtAnalysing}
                className="px-4 py-2 bg-text border border-text text-white rounded-md text-[12.5px] font-medium cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="approve-button"
              >
                {isStepThrough ? '✓ Approve & continue' : '✓ Approve & resume'}
              </button>
              <button
                type="button"
                onClick={() => submit('denied')}
                disabled={result.isLoading || !reasonFilled || doubtAnalysing}
                className="px-4 py-2 bg-err border border-err text-white rounded-md text-[12.5px] font-medium cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="deny-button"
              >
                {isStepThrough ? '✕ Reject & stop' : '✕ Deny & fail run'}
              </button>
              {doubtAnalysing ? (
                <span className="text-[11.5px] text-text-muted">
                  Paused while your concern is investigated.
                </span>
              ) : null}
            </>
          ) : (
            <DisabledDecide reason={denyReason} />
          )}
          {result.isSuccess ? (
            <span
              className={`ml-auto text-[11.5px] ${submittedDecision === 'approved' ? 'text-ok' : 'text-err'}`}
              data-testid="approval-result"
            >
              Decision recorded:{' '}
              <strong>{submittedDecision}</strong> at {formatTimestamp(result.data?.decided_at ?? null)}
            </span>
          ) : null}
          {result.isError ? (
            <span className="ml-auto text-[11.5px] text-err" data-testid="approval-error">
              {decideErrorMessage(result.error)}
            </span>
          ) : null}
        </div>
        <DoubtPanel approvalId={approval.approval_id} canDecide={canDecide} />
      </div>
    </article>
  );
}

function DisabledDecide({ reason }: { reason?: string }): JSX.Element {
  const [hover, setHover] = useState<'approve' | 'deny' | null>(null);
  const tip = reason && reason.length > 0 ? reason : 'Only an Admin or Approver can decide this approval.';
  return (
    <>
      <span className="relative inline-flex">
        <button
          type="button"
          disabled
          onMouseEnter={() => setHover('approve')}
          onMouseLeave={() => setHover(null)}
          className="px-4 py-2 bg-panel-3 border border-border text-text-muted rounded-md text-[12.5px] font-medium cursor-not-allowed"
          data-testid="approve-button-disabled"
        >
          ✓ Approve & resume
        </button>
        {hover === 'approve' ? (
          <span
            className="absolute bottom-full left-0 mb-1 bg-text border border-text px-3 py-2 rounded-md text-[11.5px] text-white whitespace-nowrap z-10"
            role="tooltip"
          >
            {tip}
          </span>
        ) : null}
      </span>
      <span className="relative inline-flex">
        <button
          type="button"
          disabled
          onMouseEnter={() => setHover('deny')}
          onMouseLeave={() => setHover(null)}
          className="px-4 py-2 bg-panel-3 border border-border text-text-muted rounded-md text-[12.5px] font-medium cursor-not-allowed"
          data-testid="deny-button-disabled"
        >
          ✕ Deny & fail run
        </button>
        {hover === 'deny' ? (
          <span
            className="absolute bottom-full left-0 mb-1 bg-text border border-text px-3 py-2 rounded-md text-[11.5px] text-white whitespace-nowrap z-10"
            role="tooltip"
          >
            {tip}
          </span>
        ) : null}
      </span>
      <span className="text-[11.5px] text-text-muted" data-testid="approval-disabled-reason">
        disabled — {tip}
      </span>
    </>
  );
}

function Field({
  label,
  value,
  mono = false,
  warn = false
}: {
  label: string;
  value: string;
  mono?: boolean;
  warn?: boolean;
}): JSX.Element {
  return (
    <div>
      <div className="text-[10.5px] text-text-muted uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-[12.5px] ${warn ? 'text-warn font-medium' : 'text-text'} ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </div>
    </div>
  );
}
